import { capLeave, isArtistOffOn, isLiveBooking, leaveBlockDayKeys, studioDayKey } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { eventBus } from '@/events/eventBus';
import { addLeaveBlock, buildDayOffBlock, removeDayOffDate, type DayOffInput } from '@/domain/artistAdmin';
import { persistArtist } from '@/api/persist';
import type { Artist, Booking, LeaveBlock } from '@/domain/types';

/**
 * D10-02: would this leave take the artist past their annual allowance? The
 * message to show, or null when it fits (working days per calendar year; RDO and
 * comp days are free; no allowance set = no cap). The server checks the same
 * rule on PUT /api/artists.
 */
export function leaveCapMessage(artist: Pick<Artist, 'availability' | 'leaveAllowanceDays'>, block: LeaveBlock): string | null {
  const res = capLeave(artist.availability, artist.leaveAllowanceDays, block);
  return res.ok ? null : res.message;
}

/**
 * D10-02: the artist's live bookings on the days a leave block covers, so the
 * person adding the leave is told to move them (it never cancels anything).
 */
export function bookingsDuringLeave(
  artistId: string,
  block: Pick<LeaveBlock, 'startDate' | 'endDate'>,
  bookings: Record<string, Booking>,
): Booking[] {
  const days = new Set(leaveBlockDayKeys(block));
  return Object.values(bookings)
    .filter((b) => b.artistId === artistId && isLiveBooking(b) && days.has(studioDayKey(b.start)))
    .sort((a, b) => a.start.localeCompare(b.start));
}

/** D10-02: is the artist on leave (a dated day off) on this studio day? */
export function artistOnLeave(artist: Pick<Artist, 'availability'> | undefined, dayOrIso: string): boolean {
  return artist ? isArtistOffOn(artist.availability, dayOrIso) : false;
}

/**
 * Save an artist whose leave changed: optimistic, then reverted to `prev` with
 * the server's reason if PUT /api/artists refuses it (D10-02: over the allowance).
 */
export function saveArtistLeave(next: Artist, prev: Artist): Promise<boolean> {
  useBrainStore.getState().upsertArtist(next);
  return persistArtist(next, {
    onReject: (_status, body) => {
      const cur = useBrainStore.getState().artists[next.id];
      useBrainStore.getState().upsertArtist({ ...(cur ?? next), availability: prev.availability });
      useUIStore.getState().pushNotification({
        intensity: 'high',
        title: "Leave wasn't saved",
        body: body?.message ?? 'The server refused this leave.',
        ttlMs: 8000,
      });
    },
  }).then((o) => o === 'ok');
}

/**
 * Create a dated day-off (RDO / comp / leave) on an artist's record. Single
 * write path shared by the calendar and the profile so both stay in sync.
 * Persists through the artist write route (PUT /api/artists/:id) so the day off
 * survives a reload — the store update alone was lost (14ym9bv75kw).
 * Returns the created block id, or null if the artist is unknown or the leave
 * would go past their annual allowance (D10-02; see leaveCapMessage).
 */
export function scheduleDayOff(artistId: string, input: DayOffInput): string | null {
  const brain = useBrainStore.getState();
  const artist = brain.artists[artistId];
  if (!artist) return null;
  const id = crypto.randomUUID();
  const block = buildDayOffBlock(input, id);
  if (leaveCapMessage(artist, block)) return null;
  const next = { ...artist, availability: addLeaveBlock(artist.availability, block) };
  void saveArtistLeave(next, artist);
  eventBus.publish('artist:day-off-scheduled', { artistId, kind: input.kind, date: input.date });
  return id;
}

/** Do not dismiss the modal or show a working day until the server saves it. */
export async function removeDayOff(artistId: string, date: string): Promise<boolean> {
  const artist = useBrainStore.getState().artists[artistId];
  if (!artist) return false;
  const next = { ...artist, availability: removeDayOffDate(artist.availability, date) };
  if (await persistArtist(next) !== 'ok') return false;
  useBrainStore.getState().upsertArtist(next);
  return true;
}
